
import React, { useState, useEffect } from 'react';
import ProgressCalendar from '../components/ProgressCalendar';
import { MOCK_RESOURCES, TEXT } from '../constants';
import { User, UserProgress, Resource, SubjectCategory } from '../types';
import { ArrowRight, BookOpen, Users, Globe, Award, Sparkles, Shield, CheckCircle, ChevronRight, Play } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface HomeProps {
  user: User;
  progress: UserProgress;
  onNavigateToSubject: (subject: SubjectCategory) => void;
}

const Home: React.FC<HomeProps> = ({ user, progress, onNavigateToSubject }) => {
  const [slideIndex, setSlideIndex] = useState(0);
  
  const slides = [
    {
      image: "https://images.unsplash.com/photo-1509062522246-3755977927d7?q=80&w=2000&auto=format&fit=crop",
      title: TEXT.heroSubtitle,
      tag: TEXT.heroTag1
    },
    {
      image: "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?q=80&w=2000&auto=format&fit=crop",
      title: "Монголын эрдэм шинжилгээний шилдэг",
      tag: TEXT.heroTag2
    },
    {
      image: "https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?q=80&w=2000&auto=format&fit=crop",
      title: "Мэдлэгийн зөрүүг арилгах",
      tag: TEXT.heroTag3
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setSlideIndex((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  return (
    <div className="space-y-32 animate-in fade-in duration-700 w-full pb-40">
      
      {/* Hero Section - Editorial Style */}
      <section id="hero-section" className="relative h-[85vh] w-full rounded-[4.5rem] overflow-hidden group shadow-2xl">
        <AnimatePresence mode="wait">
          <motion.div 
            key={slideIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
            className="absolute inset-0"
          >
            <img src={slides[slideIndex].image} className="w-full h-full object-cover scale-110 slideshow-zoom" alt="Hero" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent"></div>
          </motion.div>
        </AnimatePresence>
        
        <div className="absolute inset-0 flex flex-col justify-end p-12 md:p-24 space-y-8">
          <div className="flex items-center gap-4">
            <span className="bg-primary text-white px-8 py-2.5 rounded-full text-[10px] font-bold uppercase tracking-[0.4em] shadow-xl">
              {slides[slideIndex].tag}
            </span>
            <div className="h-px w-24 bg-white/30"></div>
          </div>

          <h1 className="text-6xl md:text-8xl font-serif text-white tracking-tighter leading-[0.85] max-w-6xl">
            {TEXT.heroTitle}
          </h1>

          <p className="text-white/70 text-xl md:text-3xl font-medium max-w-3xl leading-relaxed">
            {slides[slideIndex].title}
          </p>

          <div className="flex flex-wrap gap-4 pt-8">
            <button 
              id="library-button"
              onClick={() => onNavigateToSubject('All' as any)}
              className="px-12 py-6 bg-white text-slate-900 rounded-full font-bold uppercase tracking-widest text-[10px] hover:bg-primary hover:text-white transition-all shadow-2xl flex items-center gap-4"
            >
              Номын санг үзэх <ArrowRight size={18} />
            </button>
            <button className="px-12 py-6 bg-white/10 backdrop-blur-md text-white border border-white/20 rounded-full font-bold uppercase tracking-widest text-[10px] hover:bg-white/20 transition-all">
              Бидний тухай
            </button>
          </div>
        </div>

        <div className="absolute bottom-12 right-12 md:right-24 flex gap-4">
          {slides.map((_, i) => (
            <button 
              key={i} 
              onClick={() => setSlideIndex(i)}
              className={`h-1.5 transition-all duration-500 rounded-full ${i === slideIndex ? 'w-16 bg-primary' : 'w-6 bg-white/20 hover:bg-white/40'}`}
            />
          ))}
        </div>
      </section>

      {/* About Us Section */}
      <section className="max-w-4xl space-y-12">
        <div className="space-y-6">
          <div className="flex items-center gap-3 text-primary">
            <Sparkles size={20} />
            <span className="text-[10px] font-bold uppercase tracking-[0.4em]">Бидний тухай</span>
          </div>
          <h2 className="text-5xl md:text-7xl font-serif text-slate-800 tracking-tighter leading-none">Anir-ын түүх</h2>
          <p className="text-muted text-xl md:text-2xl font-medium leading-relaxed">
            Anir төсөл нь 2024 онд Монгол сурагчдын дунд мэдлэгийн хүртээмжийг нэмэгдүүлэх, үе тэнгийнхнээсээ суралцах боломжийг бүрдүүлэх зорилготойгоор байгуулагдсан. Бид сурагч бүрт чанартай эх сурвалж, баталгаажсан зөвлөгөөг хүргэхийг зорьдог.
          </p>
        </div>
      </section>

      {/* Success Section - Combined Stats and Maps */}
      <section id="impact-section" className="space-y-20">
        <div className="text-center space-y-4">
          <span className="text-[10px] font-bold text-secondary uppercase tracking-[0.5em]">Бидний амжилт</span>
          <h2 className="text-4xl md:text-6xl font-serif tracking-tighter">Хүрсэн үр дүн</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-white border border-slate-100 p-10 rounded-[3rem] shadow-sm text-center space-y-2">
            <div className="text-5xl font-serif text-primary">12,000+</div>
            <div className="text-[10px] font-bold uppercase tracking-widest opacity-40">Хүрсэн сурагчид</div>
          </div>
          <div className="bg-white border border-slate-100 p-10 rounded-[3rem] shadow-sm text-center space-y-2">
            <div className="text-5xl font-serif text-secondary">450+</div>
            <div className="text-[10px] font-bold uppercase tracking-widest opacity-40">Бэлтгэсэн хөтөлбөр</div>
          </div>
          <div className="bg-white border border-slate-100 p-10 rounded-[3rem] shadow-sm text-center space-y-2">
            <div className="text-5xl font-serif text-slate-900">150+</div>
            <div className="text-[10px] font-bold uppercase tracking-widest opacity-40">Баталгаажсан багш нар</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-end">
          <div className="space-y-8">
            <p className="text-muted text-xl font-medium leading-relaxed">
              Anir төсөл нь Монгол орны өнцөг булан бүрт буй сурагчдад чанартай боловсрол, үе тэнгийнхний дэмжлэгийг хүргэхээр зорин ажиллаж байна.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-8">
            <div className="bg-slate-50 p-10 rounded-[3rem] space-y-2">
              <div className="text-5xl font-serif text-primary">21</div>
              <div className="text-[10px] font-bold uppercase tracking-widest opacity-40">Аймаг</div>
            </div>
            <div className="bg-slate-50 p-10 rounded-[3rem] space-y-2">
              <div className="text-5xl font-serif text-secondary">9</div>
              <div className="text-[10px] font-bold uppercase tracking-widest opacity-40">Дүүрэг</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-12">
          <div className="bg-white p-12 rounded-[4rem] border border-slate-100 shadow-xl space-y-8 group">
            <div className="flex justify-between items-center">
              <h4 className="text-3xl font-serif">Монгол улс</h4>
              <Globe className="text-primary opacity-20 group-hover:opacity-100 transition-opacity" size={32} />
            </div>
            <div className="aspect-video rounded-[2.5rem] overflow-hidden border border-slate-50 shadow-inner">
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/thumb/f/f3/Mongolia_provinces_map.png/800px-Mongolia_provinces_map.png" 
                alt="Mongolia Map" 
                className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700"
                referrerPolicy="no-referrer"
              />
            </div>
          </div>
          <div className="bg-white p-12 rounded-[4rem] border border-slate-100 shadow-xl space-y-8 group">
            <div className="flex justify-between items-center">
              <h4 className="text-3xl font-serif">Улаанбаатар хот</h4>
              <Globe className="text-secondary opacity-20 group-hover:opacity-100 transition-opacity" size={32} />
            </div>
            <div className="aspect-video rounded-[2.5rem] overflow-hidden border border-slate-50 shadow-inner">
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/thumb/d/d3/Ulaanbaatar_districts.png/800px-Ulaanbaatar_districts.png" 
                alt="Ulaanbaatar Map" 
                className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700"
                referrerPolicy="no-referrer"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="space-y-16">
        <div className="text-center space-y-4">
          <span className="text-[10px] font-bold text-secondary uppercase tracking-[0.5em]">Бидний ард</span>
          <h2 className="text-4xl md:text-6xl font-serif tracking-tighter">Төслийн баг</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { name: "А. Тэмүүлэн", role: "Үүсгэн байгуулагч" },
            { name: "Б. Наран", role: "Агуулгын менежер" },
            { name: "Г. Болд", role: "Технологийн ахлах" },
            { name: "Д. Сарнай", role: "Олон нийтийн харилцаа" }
          ].map((member, i) => (
            <div key={i} className="bg-white border border-slate-100 p-10 rounded-[3rem] shadow-sm hover:shadow-xl transition-all text-center space-y-2 group">
              <h4 className="text-xl font-serif">{member.name}</h4>
              <p className="text-[10px] font-bold text-primary uppercase tracking-widest">{member.role}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-slate-900 text-white p-16 md:p-32 rounded-[5rem] text-center space-y-12 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_50%,rgba(79,70,229,0.1),transparent)]"></div>
        <div className="relative z-10 space-y-8">
          <h2 className="text-5xl md:text-8xl font-serif tracking-tighter leading-none">Ирээдүйгээ хамтдаа бүтээе</h2>
          <p className="text-white/60 text-xl md:text-3xl font-medium max-w-4xl mx-auto leading-relaxed">
            Мянга мянган сурагчидтай нэгдэж, мэдлэгээ хуваалцаж, хамтдаа хөгжих боломжийг бүү алдаарай.
          </p>
          <div className="flex flex-wrap justify-center gap-6 pt-8">
            <button 
              onClick={() => onNavigateToSubject('All' as any)}
              className="px-16 py-8 bg-primary text-white rounded-full font-bold uppercase tracking-widest text-xs hover:bg-secondary transition-all shadow-2xl flex items-center gap-4"
            >
              Одоо эхлэх <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
